import { Context } from './core/Context';
import { CustomEventEmitter } from './core/CustomEventEmitter';
import { TaskError } from './errors/TaskError';
import { UploaderError } from './errors/UploaderError';
import { CanceledEvent } from './events/CanceledEvent';
import { ChangesEvent } from './events/ChangesEvent';
import { FinishedEvent } from './events/FinishedEvent';
import { FinishingEvent } from './events/FinishingEvent';
import { QueuedEvent } from './events/QueuedEvent';
import { StartedEvent } from './events/StartedEvent';
import { StartingEvent } from './events/StartingEvent';
import { StatusEvent } from './events/StatusEvent';
import { UploadErrorEvent } from './events/UploadErrorEvent';
import { UploadProgressEvent } from './events/UploadProgressEvent';
import { ConnectionEvents, UploaderStatus } from './globals/constant';
import { Connections } from './modules/handlers/Connections';
import { Sequencer } from './modules/Sequencer';
export { CanceledEvent, ChangesEvent, FinishedEvent, FinishingEvent, QueuedEvent, StartedEvent, StartingEvent, StatusEvent, UploadErrorEvent, UploadProgressEvent } from './events';
import { CustomUrlAlreadyInUseError, EmailNotAllowedError, EmptyFileListError, FileReaderAbortError, FileReaderNotFoundError, FileReaderNotReadableError, FileReaderSecurityError, FileReaderUnknownError, FileSystemAbortError, FileSystemNotFoundError, FileSystemPermissionDeniedError, FileSystemUnknownError, InvalidAvailabilityDurationError, InvalidDeliveryError, InvalidParameterError, InvalidSubscriptionError, MissingReceiversError, MissingSenderError, NetworkError, OwnerUsageExceededError, PasswordRequiredError, TransferAlreadyCanceledError, TransferAlreadyCancelingError, TransferAlreadyFinishedError, TransferAlreadyFinishingError, TransferAlreadyPausedError, TransferAlreadyQueuedError, TransferAlreadyStartedError, TransferAlreadyStartingError, TransferIsInErrorError, TransferNotFoundError, UnauthorizedError, UnknownError, UnsupportedFileSourceError, UnsupportedFileTypeError, UnvalidTokenError, UsageExceededError } from './errors/errors';
import { PausedEvent } from './events/PausedEvent';
import { QueueFinished } from './events/QueueFinished';
import { QueueDeletedEvent } from './events/QueueDeletedEvent';
import { ResumableEvent } from './events/ResumableEvent';
export const errors = {
    EmptyFileListError,
    FileReaderAbortError,
    FileReaderNotReadableError,
    FileReaderSecurityError,
    FileReaderUnknownError,
    FileReaderNotFoundError,
    InvalidParameterError,
    CustomUrlAlreadyInUseError,
    UnauthorizedError,
    UnvalidTokenError,
    PasswordRequiredError,
    EmailNotAllowedError,
    FileSystemAbortError,
    FileSystemNotFoundError,
    FileSystemPermissionDeniedError,
    FileSystemUnknownError,
    InvalidAvailabilityDurationError,
    InvalidDeliveryError,
    InvalidSubscriptionError,
    MissingReceiversError,
    MissingSenderError,
    UnsupportedFileSourceError,
    UnsupportedFileTypeError,
    UsageExceededError,
    OwnerUsageExceededError,
    NetworkError,
    TransferAlreadyQueuedError,
    TransferAlreadyStartingError,
    TransferAlreadyCanceledError,
    TransferAlreadyCancelingError,
    TransferAlreadyFinishedError,
    TransferAlreadyFinishingError,
    TransferAlreadyPausedError,
    TransferAlreadyStartedError,
    TransferIsInErrorError,
    TransferNotFoundError,
    UnknownError,
    UploaderError,
};
export { UploaderError } from './errors/UploaderError';
export { UploaderEvents, UploaderStatus } from './globals/constant';
export class SmashUploader extends CustomEventEmitter {
    constructor(uploaderParameters) {
        super();
        this.initiaParallelConnections = 1;
        this.context = new Context(uploaderParameters);
        this.connections = new Connections();
        this.sequencer = new Sequencer();
        this.bindConnections();
    }
    bindConnections() {
        this.connections.on(ConnectionEvents.ConnectionAvailable, (event) => this.handleConnectionAvailable(this, event));
        this.connections.on(ConnectionEvents.ConnectionBusy, this.handleConnectionBusy);
        return this;
    }
    reset() {
        this.resetTimers();
        this.connections.off();
        this.connections = new Connections();
        this.bindConnections();
        this.context.reset();
        return this;
    }
    emitChanges(event) {
        const changesEvent = new ChangesEvent({ event });
        this.emit(changesEvent.name, changesEvent);
    }
    emitStatus() {
        const statusEvent = new StatusEvent(this.context);
        this.emit(statusEvent.name, statusEvent);
        this.emitChanges(statusEvent);
    }
    emitStarting() {
        const context = this.context.starting();
        this.emitStatus();
        const startingEvent = new StartingEvent(context);
        this.emit(startingEvent.name, startingEvent);
        this.emitChanges(startingEvent);
    }
    emitStarted() {
        const context = this.context.started();
        this.emitStatus();
        const startedEvent = new StartedEvent(context);
        this.emit(startedEvent.name, startedEvent);
        this.emitChanges(startedEvent);
        this.startProgressTimer();
        this.startSpeedTimer();
    }
    startQueueTimer() {
        this.queueTimer = setInterval(() => {
            if (this.context.localEndOfQueueTimestamp < Date.now()) {
                this.emitQueueFinished();
                clearInterval(this.queueTimer);
            }
        }, 1000);
    }
    emitQueue() {
        const context = this.context.queued();
        this.emitStatus();
        const queuedEvent = new QueuedEvent(context);
        this.emit(queuedEvent.name, queuedEvent);
        this.emitChanges(queuedEvent);
    }
    emitQueueFinished() {
        const context = this.context.resumable();
        this.emitStatus();
        const queueFinishedEvent = new QueueFinished(context);
        this.emit(queueFinishedEvent.name, queueFinishedEvent);
        this.emitChanges(queueFinishedEvent);
    }
    emitQueueDeleted() {
        const queueDeletedEvent = new QueueDeletedEvent(this.context);
        this.emit(queueDeletedEvent.name, queueDeletedEvent);
        this.emitChanges(queueDeletedEvent);
    }
    emitResumable() {
        const context = this.context.resumable();
        this.emitStatus();
        const resumableEvent = new ResumableEvent(context);
        this.emit(resumableEvent.name, resumableEvent);
        this.emitChanges(resumableEvent);
    }
    emitPaused() {
        const context = this.context.paused();
        this.emitStatus();
        const pausedEvent = new PausedEvent(context);
        this.emit(pausedEvent.name, pausedEvent);
        this.emitChanges(pausedEvent);
    }
    emitProgress() {
        const progressEvent = new UploadProgressEvent(this.context);
        this.emit(progressEvent.name, progressEvent);
        this.emitChanges(progressEvent);
    }
    emitFinishing() {
        this.emitProgress();
        const context = this.context.finishing();
        this.emitStatus();
        const finishingEvent = new FinishingEvent(context);
        this.emit(finishingEvent.name, finishingEvent);
        this.emitChanges(finishingEvent);
    }
    emitFinished() {
        const context = this.context.finished();
        this.emitStatus();
        const finishedEvent = new FinishedEvent(context);
        this.emit(finishedEvent.name, finishedEvent);
        this.emitChanges(finishedEvent);
        this.resetTimers();
        this.uploadPromise.resolve({
            status: this.context.getStatus(),
            transfer: {
                id: this.context.transfer.id,
                status: this.context.transfer.status,
                region: this.context.transfer.region,
                transferUrl: this.context.transfer.transferUrl,
                uploadState: this.context.transfer.uploadState,
                availabilityEndDate: this.context.transfer.availabilityEndDate,
                availabilityDuration: this.context.transfer.availabilityDuration,
                availabilityStartDate: this.context.transfer.availabilityStartDate,
                preview: this.context.transfer.preview,
                created: this.context.transfer.created,
                modified: this.context.transfer.modified,
                size: this.context.transfer.size,
                filesNumber: this.context.transfer.filesNumber,
            }
        });
    }
    emitCanceled() {
        const context = this.context.cancel();
        this.emitStatus();
        this.resetTimers();
        const canceledEvent = new CanceledEvent(context);
        this.emit(canceledEvent.name, canceledEvent);
        this.emitChanges(canceledEvent);
        this.uploadPromise.resolve({
            status: this.context.getStatus(),
            transfer: this.context.transfer ? {
                id: this.context.transfer.id,
                status: this.context.transfer.status,
                region: this.context.transfer.region,
                transferUrl: this.context.transfer.transferUrl,
                uploadState: this.context.transfer.uploadState,
                availabilityEndDate: this.context.transfer.availabilityEndDate,
                availabilityDuration: this.context.transfer.availabilityDuration,
                availabilityStartDate: this.context.transfer.availabilityStartDate,
                preview: this.context.transfer.preview,
                created: this.context.transfer.created,
                modified: this.context.transfer.modified,
                size: this.context.transfer.size,
                filesNumber: this.context.transfer.filesNumber,
            } : undefined
        });
        this.reset();
    }
    emitError(error) {
        this.context.error();
        this.emitStatus();
        if (error instanceof TaskError) {
            const errorEvent = new UploadErrorEvent({ error: error.getPublicError() });
            this.emit(errorEvent.name, errorEvent);
            this.emitChanges(errorEvent);
            this.uploadPromise.reject(error.getPublicError());
        }
        else if (error instanceof Error) {
            this.uploadPromise.reject(new UploaderError(error));
        }
        else {
            this.uploadPromise.reject(new UploaderError('Unknown error'));
        }
        this.reset();
    }
    startTask(connection, task) {
        if (this.context.shouldStopProcessTask() === false) {
            const preProcessedTask = task.preProcess(this.context);
            if (preProcessedTask) {
                if (preProcessedTask.isLastTask()) {
                    this.emitFinishing();
                }
                this.processTask(connection, preProcessedTask);
            }
        }
    }
    processTask(connection, task) {
        connection.execute(task).then(processedTask => {
            if (this.context.isCanceled() || this.context.isOnError()) {
                return;
            }
            if (processedTask.isOnError()) {
                this.handleConnectionError(processedTask);
            }
            else {
                const nextTask = processedTask.postProcess(this.context);
                if (processedTask.isFirstTask()) {
                    this.addConnections(this.context.transfer.parallelConnections);
                    if (this.context.hasQueue()) {
                        this.emitQueue();
                        this.startQueueTimer();
                    }
                    else {
                        this.emitStarted();
                    }
                }
                else if (processedTask.isLastTask()) {
                    this.emitFinished();
                }
                if (nextTask) {
                    if (nextTask.isLastTask()) {
                        this.emitFinished();
                    }
                    else {
                        this.startTask(connection, nextTask);
                    }
                }
                else {
                    if (this.context.isPaused()) {
                        return;
                    }
                    this.pingConnections();
                }
            }
        });
    }
    handleConnectionAvailable(uploader, event) {
        const connection = uploader.connections.get(event.connectionId);
        const nextTask = uploader.sequencer.evaluateNextTask(this.context);
        if (nextTask) {
            uploader.startTask(connection, nextTask);
        }
    }
    handleConnectionBusy(event) {
        const task = event.getTask();
        if (task) {
            this.context.taskQueue.add(task);
        }
    }
    handleConnectionError(task) {
        const error = task.processError();
        if (error.shouldAbortUpload()) {
            this.context.unrecoverableUploadError();
            this.emitError(error);
        }
        else {
            const recoveryTask = error.getRecoveryTask();
            if (recoveryTask) {
                this.context.taskQueue.add(recoveryTask);
            }
            this.pingConnections();
        }
    }
    computePercent() {
        var _a, _b, _c, _d;
        if (((_b = (_a = this.context) === null || _a === void 0 ? void 0 : _a.transfer) === null || _b === void 0 ? void 0 : _b.size) && ((_d = (_c = this.context) === null || _c === void 0 ? void 0 : _c.transfer) === null || _d === void 0 ? void 0 : _d.size) > 0) {
            this.context.percent = Math.round((this.context.uploadedBytes / this.context.transfer.size) * 100);
        }
    }
    computeSpeed() {
        this.context.speed = this.context.uploadedBytes - this.context.lastValueUploadedBytes;
        this.context.lastValueUploadedBytes = this.context.uploadedBytes;
    }
    computeEstimatedTime() {
        if (this.context.transfer && this.context.speed > 0) {
            this.context.estimatedTime = (this.context.transfer.size / this.context.speed) * 1000;
        }
    }
    computeRemainingTime() {
        if (this.context.transfer && this.context.speed > 0) {
            this.context.remainingTime = ((this.context.transfer.size - this.context.uploadedBytes) / this.context.speed) * 1000;
        }
    }
    startSpeedTimer() {
        this.speedTimer = setInterval(() => {
            this.computeSpeed();
            this.computeEstimatedTime();
            this.computeRemainingTime();
        }, 1000);
    }
    startProgressTimer() {
        this.progressTimer = setInterval(() => {
            this.computePercent();
            this.emitProgress();
        }, 100);
    }
    endProgressTimer() {
        clearInterval(this.progressTimer);
    }
    endSpeedTimer() {
        clearInterval(this.speedTimer);
    }
    endQueueTimer() {
        clearInterval(this.queueTimer);
    }
    resetTimers() {
        this.endProgressTimer();
        this.endSpeedTimer();
        this.endQueueTimer();
    }
    pingConnections() {
        this.connections.ping();
        return this;
    }
    addConnections(count) {
        const newConnections = this.connections.updateConnectionNumber(count);
        newConnections.forEach((connection) => connection.wakeUp());
        return this;
    }
    upload(params) {
        return new Promise((resolve, reject) => {
            try {
                this.uploadPromise = { resolve, reject };
                if (this.context.getStatus() === UploaderStatus.Queued) {
                    throw new TransferAlreadyQueuedError('Unable to upload, transfer is already in queue.');
                }
                if (this.context.getStatus() === UploaderStatus.Starting) {
                    throw new TransferAlreadyStartingError('Unable to upload, transfer is already starting.');
                }
                if (this.context.getStatus() === UploaderStatus.Started) {
                    throw new TransferAlreadyStartedError('Unable to upload, transfer is already started.');
                }
                if (this.context.getStatus() === UploaderStatus.Finishing) {
                    throw new TransferAlreadyFinishingError('Unable to upload, transfer is already finishing.');
                }
                if (this.context.getStatus() === UploaderStatus.Finished) {
                    throw new TransferAlreadyFinishedError('Unable to upload, transfer is already finished.');
                }
                if (this.context.getStatus() === UploaderStatus.Canceling) {
                    throw new TransferAlreadyCancelingError('Unable to upload, transfer is already canceling.');
                }
                if (this.context.getStatus() === UploaderStatus.Canceled) {
                    throw new TransferAlreadyCanceledError('Unable to upload, transfer is already canceled.');
                }
                if (this.context.getStatus() === UploaderStatus.Paused) {
                    throw new TransferAlreadyPausedError('Unable to upload, transfer is already paused.');
                }
                if (this.context.getStatus() === UploaderStatus.Error) {
                    throw new TransferIsInErrorError('Unable to upload, transfer is in error.');
                }
                if (this.context.getStatus() === UploaderStatus.Resumable) {
                    throw new TransferAlreadyStartedError('Unable to upload, transfer is already started');
                }
                if (!params.files.length) {
                    throw new EmptyFileListError('You must add some files to create a Transfer');
                }
                this.emitStarting();
                this.context.createTransfer(params);
                this.addConnections(this.initiaParallelConnections);
            }
            catch (error) {
                this.emitError(error);
            }
        });
    }
    resume() {
        return new Promise(resolve => {
            try {
                if (this.context.isResumable() || this.context.isPaused()) {
                    this.emitStarted();
                    this.pingConnections();
                }
                if (this.context.isQueued()) {
                    this.emitQueue();
                }
                resolve({
                    status: this.context.getStatus(),
                    transfer: {
                        id: this.context.transfer.id,
                        status: this.context.transfer.status,
                        region: this.context.transfer.region,
                        transferUrl: this.context.transfer.transferUrl,
                        uploadState: this.context.transfer.uploadState,
                        availabilityDuration: this.context.transfer.availabilityDuration,
                        preview: this.context.transfer.preview,
                        created: this.context.transfer.created,
                        modified: this.context.transfer.modified,
                        size: this.context.transfer.size,
                        filesNumber: this.context.transfer.filesNumber,
                        queue: this.context.transfer.queue,
                        queuedUntil: this.context.transfer.queuedUntil,
                    }
                });
            }
            catch (error) {
                this.emitError(error);
            }
        });
    }
    pause() {
        return new Promise(resolve => {
            try {
                if (this.context.isStarted()) {
                    this.emitPaused();
                }
                resolve({
                    status: this.context.getStatus(),
                    transfer: {
                        id: this.context.transfer.id,
                        status: this.context.transfer.status,
                        region: this.context.transfer.region,
                        transferUrl: this.context.transfer.transferUrl,
                        uploadState: this.context.transfer.uploadState,
                        availabilityDuration: this.context.transfer.availabilityDuration,
                        preview: this.context.transfer.preview,
                        created: this.context.transfer.created,
                        modified: this.context.transfer.modified,
                        size: this.context.transfer.size,
                        filesNumber: this.context.transfer.filesNumber,
                        queue: this.context.transfer.queue,
                        queuedUntil: this.context.transfer.queuedUntil,
                    }
                });
            }
            catch (error) {
                this.emitError(error);
            }
        });
    }
    checkTransferUpdatable() {
        const status = this.context.getStatus();
        switch (status) {
            case UploaderStatus.Pending:
                throw new TransferAlreadyStartingError('Unable to update transfer, upload is pending.');
            case UploaderStatus.Starting:
                throw new TransferAlreadyStartingError('Unable to update transfer, upload is starting.');
            case UploaderStatus.Finishing:
                throw new TransferAlreadyFinishingError('Unable to update transfer, upload is finishing.');
            case UploaderStatus.Finished:
                throw new TransferAlreadyFinishedError('Unable to update transfer, upload is finished.');
            case UploaderStatus.Canceling:
                throw new TransferAlreadyCancelingError('Unable to update transfer, upload is canceling.');
            case UploaderStatus.Canceled:
                throw new TransferAlreadyCanceledError('Unable to update transfer, upload is canceled.');
            case UploaderStatus.Error:
                throw new TransferIsInErrorError('Unable to update transfer, upload is in error.');
        }
    }
    update(params) {
        return new Promise(async (resolve, reject) => {
            try {
                this.checkTransferUpdatable();
                const updatedTransfer = await this.context.updateTransfer(params);
                if ((!updatedTransfer.transfer.queue || updatedTransfer.transfer.queue === 0) && this.context.status === UploaderStatus.Queued) {
                    this.emitQueueDeleted();
                    this.emitResumable();
                }
                resolve(updatedTransfer);
            }
            catch (error) {
                reject(error);
            }
        });
    }
    cancel() {
        if (this.context.isCanceled()) {
            return;
        }
        this.context.cancel();
        this.connections.reset();
        this.connections.forEach((connection) => {
            connection.reset();
        });
        this.resetTimers();
        this.emitCanceled();
    }
}
